import { UserCircle } from 'lucide-react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@reflex/ui/components/ui/hover-card';
import { RESOURCE_PREVIEW_HOVER_CONTENT_CLASS } from '@reflex/ui/components/resource-preview/ResourcePreviewCard';
import { ResourcePreview } from '@reflex/ui/components/resource-preview/ResourcePreview';
import { cn } from '@reflex/ui/lib/utils';
import { renderPersonaIcon } from './PersonaIconPicker.js';
import type { AgentPersona } from '../shared/types.js';

export interface PersonaListIconProps {
  persona: AgentPersona;
  /** Tailwind size classes for the rendered icon. Defaults to `h-4 w-4`. */
  className?: string;
}

/**
 * Renders the persona's chosen icon (lucide or emoji) wrapped in a
 * `HoverCard` that opens the registered `personas` resource preview —
 * mirrors the same hover-to-preview UX `OwnerChip` ships for users and
 * teams. Used by every palette/list row that surfaces a persona so the
 * row identity matches the icon picked in `AgentPersonasPage` and users
 * can peek at metadata without leaving the list.
 */
export function PersonaListIcon({ persona, className = 'h-4 w-4' }: PersonaListIconProps) {
  const rendered = renderPersonaIcon(persona.icon, className);
  const fallback = <UserCircle className={className} aria-hidden />;

  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>
        <span
          // `img`: a bare span may not carry `aria-label`
          // (axe `aria-prohibited-attr`); the icon is a non-interactive glyph.
          role="img"
          aria-label={`Preview persona ${persona.name}`}
          data-testid={`persona-list-icon-${persona.id}`}
          className={cn(
            'inline-flex shrink-0 cursor-default items-center justify-center text-muted-foreground',
            'rounded-sm transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          )}
          onClick={(e) => {
            // Prevent the surrounding `CommandItem` from selecting the row
            // when the user clicks the icon to peek at the preview.
            e.stopPropagation();
          }}
        >
          {rendered ?? fallback}
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        className={RESOURCE_PREVIEW_HOVER_CONTENT_CLASS}
        aria-label={`Persona ${persona.name}`}
      >
        <ResourcePreview kind="personas" id={persona.id} fallbackName={persona.name} />
      </HoverCardContent>
    </HoverCard>
  );
}
